import { useEffect, useState } from "react";

import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
  Typography,
} from "@material-ui/core";
import { Close, ChevronLeft, ChevronRight } from "@material-ui/icons";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";
import { observer } from "mobx-react-lite";

import AnnouncementContent from "./AnnouncementContent";
import usePresignedUrl from "../../hooks/usePresignedUrl";
import useAnnouncements from "../../hooks/useAnnouncements";
import Image from "next/image";

const AUTO_SLIDE_INTERVAL = 5000;

const MobileAnnouncementCard = ({ announcement, onClick }) => {
  const imageUrl = usePresignedUrl("announcement_image", announcement?.imageUrl);

  return (
    <Paper
      elevation={2}
      onClick={onClick}
      style={{
        borderRadius: "10px",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {imageUrl && (
        <Box width="100%">
          <Image
            src={imageUrl}
            alt={announcement?.title || "announcement"}
            style={{ width: "100%", maxHeight: 140, objectFit: "cover" }}
          />
        </Box>
      )}
      {announcement?.title && (
        <Typography
          variant="h6"
          style={{ fontWeight: 600, padding: "12px 16px" }}
        >
          {announcement.title}
        </Typography>
      )}
    </Paper>
  );
};

const LoginPageAnnouncement = observer(({ initialSlide }) => {
  const { items } = useAnnouncements("login_page");
  const [slideIndex, setSlideIndex] = useState(0);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const slideCount = items.length + 1;

  useEffect(() => {
    if (slideCount <= 1) {
      return undefined;
    }
    const timer = setInterval(() => {
      setSlideIndex((index) => (index + 1) % slideCount);
    }, AUTO_SLIDE_INTERVAL);
    return () => clearInterval(timer);
  }, [slideCount]);

  const currentIndex = Math.min(slideIndex, slideCount - 1);
  const isInitial = currentIndex === 0;
  const current = isInitial ? null : items[currentIndex - 1];

  const next = () => setSlideIndex((index) => (index + 1) % slideCount);
  const prev = () =>
    setSlideIndex((index) => (index - 1 + slideCount) % slideCount);

  const navButtonStyle = {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    backgroundColor: "rgba(0, 0, 0, 0.35)",
    color: "#ffffff",
    zIndex: 2,
  };

  return (
    <Box display="flex" flexDirection="column" flex={1} minHeight={0} width="100%">
      <Box
        flex={1}
        minHeight={0}
        position="relative"
        display="flex"
        alignItems="stretch"
      >
        <Box style={{ width: "100%", visibility: isInitial ? "visible" : "hidden" }}>
          {initialSlide}
        </Box>
        {!isInitial && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            display="flex"
            alignItems="stretch"
          >
            {isSmallScreen ? (
              <MobileAnnouncementCard
                announcement={current}
                onClick={() => setSelectedAnnouncement(current)}
              />
            ) : (
              <Paper
                elevation={2}
                style={{
                  borderRadius: "10px",
                  padding: "16px 48px",
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <AnnouncementContent announcement={current} />
              </Paper>
            )}
          </Box>
        )}
        {items.length > 0 && (
          <>
            <IconButton
              onClick={prev}
              aria-label="previous announcement"
              size="small"
              style={{ ...navButtonStyle, left: 8 }}
            >
              <ChevronLeft />
            </IconButton>
            <IconButton
              onClick={next}
              aria-label="next announcement"
              size="small"
              style={{ ...navButtonStyle, right: 8 }}
            >
              <ChevronRight />
            </IconButton>
            <Box
              position="absolute"
              bottom={8}
              left={0}
              right={0}
              display="flex"
              justifyContent="center"
              alignItems="center"
              zIndex={2}
            >
              {[null, ...items].map((item, index) => (
                <Box
                  key={item?._id || "initial"}
                  onClick={() => setSlideIndex(index)}
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor:
                      index === currentIndex ? "#FFD140" : "#999999",
                    margin: "0 4px",
                    cursor: "pointer",
                  }}
                />
              ))}
            </Box>
          </>
        )}
      </Box>
      <Dialog
        open={Boolean(selectedAnnouncement)}
        onClose={() => setSelectedAnnouncement(null)}
        maxWidth="sm"
        fullWidth
      >
        <Box display="flex" justifyContent="flex-end">
          <IconButton
            onClick={() => setSelectedAnnouncement(null)}
            aria-label="close announcement"
          >
            <Close />
          </IconButton>
        </Box>
        <DialogContent>
          {selectedAnnouncement && (
            <AnnouncementContent announcement={selectedAnnouncement} />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
});

export default LoginPageAnnouncement;